import { NgIf, NgPlural, NgPluralCase, AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTableDataSource } from '@angular/material/table';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartActions, CartLineSource, CartSelectors } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { RemoveSpecialCharactersPipe } from '@next/core-lib/pipes';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumn, TableColumnTypes, TableConfig, AgrTableModule } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { LocationSiloWithProductList } from 'src/app/components/silos/models/silo-product-list.models';
import { ShopFeatureFlags } from 'src/app/shared/shop-feature-flags';

import { FavoritesStarComponent } from '../../../standalone/favorites-star/favorites-star.component';
import { decorateFormControl, isItemTypeEligibleForCartType, isUserAllowedToOrderItem } from '../../helpers/add-to-cart-helpers';
import { SkuCatalogItemViewData } from '../../models/product.models';
import { FormatAddProductToCartButtonTextPipe } from '../../pipes/format-add-product-to-cart-button-text.pipe';
import * as ProductActions from '../../store/actions';
import * as ProductSelectors from '../../store/selectors';
import { selectSkuCatalogItems } from '../../store/selectors';

export interface ProductSearchDialogData extends AbstractDialogData {
  cart?: Cart;
  silo?: LocationSiloWithProductList;
  searchTerm?: string;
  htmlButtonRef?: HTMLButtonElement;
  source: CartLineSource;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-product-search-dialog',
  templateUrl: './product-search-dialog.component.html',
  styleUrls: ['./product-search-dialog.component.scss'],
  imports: [
    AgrDialogModule,
    MatFormField,
    MatLabel,
    MatIcon,
    MatPrefix,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatIconButton,
    MatSuffix,
    NgPlural,
    NgPluralCase,
    AgrTableModule,
    MatButton,
    FavoritesStarComponent,
    AsyncPipe,
    AgrI18nSharedModule,
    FormatAddProductToCartButtonTextPipe,
  ],
})
export class ProductSearchDialogComponent extends AbstractDialog<ProductSearchDialogData> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('productSearch') productSearch: ElementRef;
  @ViewChild('productColumn') productColumn: TemplateRef<unknown>;
  @ViewChild('addToCartColumn') addToCartColumn: TemplateRef<unknown>;
  @ViewChild('addToSiloColumn') addToSiloColumn: TemplateRef<unknown>;
  @ViewChild('favoriteStarColumn') favoriteStarColumn: TemplateRef<unknown>;
  @ViewChild('mobileColumn') mobileColumn: TemplateRef<unknown>;

  hasAtLeastAdvisorRole = false;

  productSearchControl = new FormControl(this.data.searchTerm);
  orderQuantities: FormArray = new FormArray([]);

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    header: false,
    footer: false,
    scrolling: false,
    disableCards: true,
    selectColumn: false,
    pagination: false,
    columns: [],
  };
  cartId?: number;
  catalogItems: SkuCatalogItemViewData[] = [];
  customer$ = this.store.select(UserSelectors.selectCustomer);

  productSearchResults$ = this.store.select(selectSkuCatalogItems);
  public skuCatalogItemsLoaded$: Observable<boolean> = this.store.select(ProductSelectors.selectSkuCatalogItemsLoaded);
  public skuCatalogItemsNotAvailable$: Observable<boolean> = this.store.select(ProductSelectors.selectSkuCatalogItemsNotAvailable);
  public isFavoritesEnabled = false;
  public isSiloProductListMode = false;

  private readonly debounceQuantityChange$: Subject<{ row: SkuCatalogItemViewData; control: AbstractControl }> = new Subject<{
    row: SkuCatalogItemViewData;
    control: AbstractControl;
  }>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductSearchDialogData,
    @Inject(BreakpointServiceInjector) readonly breakpoint: BreakpointService,
    @Inject(EnvironmentInjector) readonly environment: AgrEnvironment,
    protected mdDialogRef: MatDialogRef<ProductSearchDialogComponent, AbstractDialogResultData<never>>,
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localeService: LocaleService,
    private readonly removeSpecialCharactersPipe: RemoveSpecialCharactersPipe,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    super();
    this.isSiloProductListMode = this.data.silo !== undefined;
  }

  ngOnInit(): void {
    this.customer$
      .pipe(
        map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
        switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
      )
      .subscribe((featureFlags) => (this.isFavoritesEnabled = featureFlags.includes(ShopFeatureFlags.enableFavorites)));

    this.mdDialogRef.afterClosed().subscribe((_) => {
      if (this.data.htmlButtonRef) {
        this.data.htmlButtonRef.blur();
      }
    });

    if (this.environment.ALLOWED_ORDER_ROLES_DISABLED) {
      this.hasAtLeastAdvisorRole = true;
    } else {
      this.store
        .select(AuthorizationSelectors.hasAtLeastAdvisorRole)
        .subscribe((hasAtLeastAdvisorRuleValue) => (this.hasAtLeastAdvisorRole = hasAtLeastAdvisorRuleValue));
    }

    this.store.dispatch(
      ProductActions.skuCatalogRequest({
        keywords: this.data.searchTerm ? this.data.searchTerm : '',
      }),
    );

    if (this.data.cart) {
      this.cartId = this.data.cart.id;
    } else {
      this.store.select(CartSelectors.selectCurrentSalesOrderCart).subscribe((cart) => (this.cartId = cart?.id));
    }

    this.productSearchControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        tap((value) =>
          this.store.dispatch(
            ProductActions.skuCatalogRequest({
              keywords: this.removeSpecialCharactersPipe.transform(value),
            }),
          ),
        ),
      )
      .subscribe();

    this.debounceQuantityChange$
      .pipe(
        debounceTime(100),
        tap(({ row, control }) => {
          const newValue = control.value as number;
          const foundItem = this.catalogItems.find((item) => item.skuId === row.skuId);
          if (foundItem?.quantity !== newValue) {
            foundItem.quantity = newValue;
          }
        }),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    combineLatest([this.productSearchResults$, this.breakpoint.xs$])
      .pipe(
        map(([catalogItems, isMobile]: [SkuCatalogItemViewData[], boolean]) => ({
          catalogItems: catalogItems?.map((catalogItem: SkuCatalogItemViewData) => ({
            ...catalogItem,
            quantity: catalogItem.quantity || catalogItem.orderingInformation.minimumOrderUnitQuantity,
          })),
          isMobile,
        })),
      )
      .subscribe(({ catalogItems, isMobile }) => {
        this.catalogItems = catalogItems;
        this.tableConfig = {
          ...this.tableConfig,
          dataSource: new MatTableDataSource(
            catalogItems?.map((catalogItem, index) => ({
              ...catalogItem,
              isCatalogItemEligibleForCart: this.isCatalogItemEligibleForCart(catalogItem),
              index,
            })),
          ),
          columns: this.createColumns(catalogItems, isMobile),
        };

        this.changeDetectorRef.detectChanges();
      });
    setTimeout(() => {
      this.productSearch.nativeElement.focus();
    }, 0);
  }

  addToCart(selectedItem: SkuCatalogItemViewData): void {
    const foundItem = this.catalogItems.find((item) => item.skuId === selectedItem.skuId);
    if (!foundItem) {
      return;
    }

    this.store.dispatch(
      CartActions.addCartLineToCart({
        cartLine: {
          skuId: foundItem.skuId,
          quantity: foundItem.quantity,
          source: this.data.source,
        },
        cartId: this.cartId,
      }),
    );
  }

  resetProductSearch(): void {
    this.productSearchControl.setValue('');
  }

  openWizardDialog(selectedItem: SkuCatalogItemViewData): void {
    const foundItem = this.catalogItems.find((item) => item.skuId === selectedItem.skuId);
    if (!foundItem || !this.isCatalogItemEligibleForCart(foundItem)) {
      return;
    }

    this.cancel();

    if (this.isSiloProductListMode) {
      this.store.dispatch(
        ProductActions.openWizardDialogForProductList({
          skuCatalogItem: foundItem,
          silo: this.data.silo,
        }),
      );
    } else {
      this.store.dispatch(
        ProductActions.openWizardDialog({
          skuCatalogItem: foundItem,
          orderType: this.data.cart?.type,
          cartId: this.cartId,
          source: this.data.source,
        }),
      );
    }
  }

  productSearchDisplay(value: SkuCatalogItemViewData): string {
    return `${value?.title ?? ''}${value?.externalSkuId ? ' (' + value?.externalSkuId + ')' : ''}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createColumns(catalogItems: SkuCatalogItemViewData[], isMobile = false): TableColumn[] {
    let columns: TableColumn[] = isMobile
      ? [
          {
            id: 'mobileColumn',
            type: TableColumnTypes.TEMPLATE,
            template: this.mobileColumn,
            className: 'flex__child--3',
          },
          {
            id: 'addToCartColumn',
            type: TableColumnTypes.TEMPLATE,
            template: this.addToCartColumn,
            className: 'flex__child--1',
          },
        ]
      : [
          {
            id: 'productColumn',
            type: TableColumnTypes.TEMPLATE,
            template: this.productColumn,
            className: 'flex__child--2',
          },
          {
            id: 'quantity',
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              callback: (row: SkuCatalogItemViewData, control: AbstractControl) => this.debounceQuantityChange$.next({ row, control }),
              formArray: this.orderQuantities,
              step: this.getQuantityStep,
              decorateFormControl: (row: SkuCatalogItemViewData, control: FormControl) => decorateFormControl(row, control, this.localeService),
              suffixArray: catalogItems?.map((catalogItem) => this.localeService.translate(`unit.${catalogItem?.orderingInformation?.orderUnit}`)) || [],
            },
            className: '',
          },
          {
            id: 'addToCartColumn',
            type: TableColumnTypes.TEMPLATE,
            template: this.addToCartColumn,
            className: 'flex__child--2',
          },
        ];

    if (this.isFavoritesEnabled) {
      columns = [
        ...columns.slice(0, columns.length - 1),
        {
          id: 'favoriteStarColumn',
          type: TableColumnTypes.TEMPLATE,
          template: this.favoriteStarColumn,
          className: 'flex__child--1 flex--justify-center',
        },
        ...columns.slice(columns.length - 1, columns.length),
      ];
    }

    if (this.isSiloProductListMode) {
      columns = [
        ...columns.slice(0, columns.length - 1),
        {
          id: 'addToSiloColumn',
          type: TableColumnTypes.TEMPLATE,
          template: this.addToSiloColumn,
          className: isMobile ? 'flex__child--1' : 'flex__child--2',
        },
      ];
    }

    return columns;
  }

  private getQuantityStep(row: SkuCatalogItemViewData): number {
    return row?.orderingInformation?.increaseOrderUnitQuantity ?? 1;
  }

  private isCatalogItemEligibleForCart(catalogItem: SkuCatalogItemViewData): boolean {
    return (
      (this.environment.ALLOWED_ORDER_ROLES_DISABLED || isUserAllowedToOrderItem(catalogItem, this.hasAtLeastAdvisorRole)) &&
      isItemTypeEligibleForCartType(catalogItem, this.data.cart?.type)
    );
  }
}
